import React, { Component } from 'react';
import { Link } from 'react-router';
import moment from 'moment';
import discountApi from '../../components/discounts/discountApi';
import { percent, shortDate } from '../../utils/discountFormat';

/**
 * OPE-169 "CMR report": per paying client — plan, add-ons, CMRR, manual and
 * campaign discounts as of a date, the rate that wins today and when it
 * expires. The CSV is built in the browser from the same rows, so no auth
 * header gymnastics are needed for a download link.
 */
export default class CmrReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            asOf: moment().format('YYYY-MM-DD'),
            country: '',
            payingOnly: true,
            reference: null,
            report: null,
            isLoading: false,
            error: null
        };
    }

    componentDidMount() {
        document.title = 'CMR report | Fergus CRM';
        discountApi.referenceData().then(reference => this.setState({ reference })).catch(() => {});
        this.load();
    }

    componentWillUnmount() {
        document.title = 'Fergus CRM';
    }

    load(e) {
        if (e) { e.preventDefault(); }
        this.setState({ isLoading: true, error: null });
        discountApi.cmrReport({ as_of: this.state.asOf, country: this.state.country || undefined, paying_only: this.state.payingOnly ? 1 : 0 })
            .then(report => this.setState({ report, isLoading: false }))
            .catch(error => this.setState({ error: error.message, isLoading: false }));
    }

    downloadCsv() {
        var { report } = this.state;
        if (!report) { return; }
        var cell = v => {
            var text = Array.isArray(v) ? v.join('; ') : (v === null || v === undefined ? '' : (v === true ? 'yes' : (v === false ? 'no' : String(v))));
            return /[",\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
        };
        var lines = [report.columns.join(',')].concat(report.rows.map(row => report.columns.map(c => cell(row[c])).join(',')));
        var blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = `cmr-discounts-${report.as_of}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    render() {
        var s = this.state;
        var countries = s.reference ? (s.reference.countries || []) : [];
        var rows = s.report ? s.report.rows : [];
        return (
            <div className="row page-todo">
                <ol className="breadcrumb page-breadcrumb">
                    <li><Link to="/discounts">Discounts</Link></li>
                    <li className="active">CMR report</li>
                </ol>
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-heading" style={{ display: 'flex', alignItems: 'center' }}>
                            <h2 style={{ flex: 1, margin: 0 }}><i className="fa fa-table"></i> CMR discount report</h2>
                            <form className="form-inline" onSubmit={this.load.bind(this)}>
                                <label style={{ marginRight: 5 }}>As of</label>
                                <input type="date" className="form-control input-sm" value={s.asOf} onChange={e => this.setState({ asOf: e.target.value })} />
                                <select className="form-control input-sm" style={{ marginLeft: 8 }} value={s.country} onChange={e => this.setState({ country: e.target.value })}>
                                    <option value="">All countries</option>
                                    {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                                </select>
                                <label className="checkbox-inline" style={{ marginLeft: 8 }}>
                                    <input type="checkbox" checked={s.payingOnly} onChange={e => this.setState({ payingOnly: e.target.checked })} /> paying only
                                </label>
                                <button type="submit" className="btn btn-sm btn-default" style={{ marginLeft: 8 }} disabled={s.isLoading}>{s.isLoading ? 'Loading...' : 'Refresh'}</button>
                                <button type="button" className="btn btn-sm btn-info" style={{ marginLeft: 8 }} disabled={!s.report || rows.length === 0} onClick={this.downloadCsv.bind(this)}><i className="fa fa-download"></i> CSV</button>
                            </form>
                        </div>
                        <div className="panel-body no-padding">
                            {s.error && <div className="alert alert-danger" style={{ margin: 10 }}>{s.error}</div>}
                            {s.report === null && !s.error && <p className="text-muted" style={{ padding: 10 }}>Loading...</p>}
                            {s.report && rows.length === 0 && <p className="text-muted" style={{ padding: 10 }}>No clients match.</p>}
                            {rows.length > 0 &&
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="table table-condensed table-hover" style={{ marginBottom: 0 }}>
                                        <thead>
                                            <tr>
                                                <th>Client</th><th>Country</th><th>Plan</th><th>Add-ons</th><th className="text-right">CMRR</th>
                                                <th className="text-right">Manual</th><th className="text-right">Account rate</th><th>Source</th><th>Ends</th><th>Scoped</th><th>Rollout</th><th>Campaign discounts</th><th>Upcoming</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map(r =>
                                                <tr key={r.client_id}>
                                                    <td><Link to={`/clients/${r.client_id}`}>{r.client_name}</Link>{!r.paying && <span className="label label-default" style={{ marginLeft: 6 }}>not paying</span>}</td>
                                                    <td>{r.country || '–'}</td>
                                                    <td>{r.plan || <span className="text-muted">none</span>}</td>
                                                    <td>{(r.add_ons || []).join(', ') || '–'}</td>
                                                    <td className="text-right">{r.cmrr === null || r.cmrr === undefined ? '–' : `${r.currency || ''} ${Number(r.cmrr).toFixed(2)}`}</td>
                                                    <td className="text-right">{percent(r.manual_discount)}</td>
                                                    <td className="text-right"><strong className={Number(r.effective_rate) > 0 ? 'text-success' : ''}>{percent(r.effective_rate)}</strong></td>
                                                    <td>{r.effective_source}</td>
                                                    <td>{r.effective_ends_on ? shortDate(r.effective_ends_on) : (Number(r.effective_rate) > 0 ? 'no end' : '–')}</td>
                                                    <td>{(r.scoped_discounts || []).length > 0 ? r.scoped_discounts.map((d, i) => <div key={i}><small>{d}</small></div>) : '–'}</td>
                                                    <td>{r.campaign_rollout === 'live' ? <span className="label label-success">live</span> : (r.campaign_rollout === 'preview' ? <span className="label label-default" title="campaign rows are not billed yet">preview</span> : <span className="text-muted">off</span>)}</td>
                                                    <td>{(r.campaign_discounts || []).map((d, i) => <div key={i}><small>{d}</small></div>)}</td>
                                                    <td>{(r.upcoming_discounts || []).map((d, i) => <div key={i}><small>{d}</small></div>)}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            }
                        </div>
                        {s.report && <div className="panel-footer text-muted">{rows.length} client{rows.length === 1 ? '' : 's'} as of {shortDate(s.report.as_of)} · account rate = highest single account-scope discount (manual or campaign), the rate every untargeted line gets; scoped grants only beat it on the lines they target; campaign rows count only for live companies</div>}
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./app/pages/discounts/CmrReport.js